import React, { useState, useEffect } from 'react';
import PersonalManager from './PersonalManager';
import VehiculosManager from './VehiculosManager';
import { API_BASE_URL, fetchWithAuth } from '../config';

export default function ContratistasManager({ usuario, forcedContratistaId = null, initialSubView = 'list' }) {
    const [contratistas, setContratistas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        identificador: '',
        nombre: '',
        razon_social: '',
        habilitado: true
    });

    const [activeSubView, setActiveSubView] = useState(initialSubView); // 'list', 'usuarios', 'personal', 'expediente'
    const [selectedContratista, setSelectedContratista] = useState(null);
    const [usuarios, setUsuarios] = useState([]);

    // Expediente States
    const [loadingExpediente, setLoadingExpediente] = useState(false);
    const [expedienteData, setExpedienteData] = useState([]);
    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [selectedForExpediente, setSelectedForExpediente] = useState(null);

    // Form States
    const [showUserForm, setShowUserForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userFormData, setUserFormData] = useState({ nombre: '', correo: '', rol_id: '' });
    const [roles, setRoles] = useState([]);

    // Sucursales States
    const [sucursales, setSucursales] = useState([]);
    const [loadingSucursales, setLoadingSucursales] = useState(false);
    const [showSucursalForm, setShowSucursalForm] = useState(false);
    const [editingSucursal, setEditingSucursal] = useState(null);
    const [sucursalFormData, setSucursalFormData] = useState({ sucursal: '', direccion: '', activo: true });

    // Dropdown de acciones por contratista
    const [openMenu, setOpenMenu] = useState(null);
    const menuRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenu(null);
            }
        };
        if (openMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openMenu]);

    const menuActionStyle = {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.85rem',
        color: 'var(--text)',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s ease'
    };

    useEffect(() => {
        if (forcedContratistaId) {
            setupForcedView();
        } else {
            fetchContratistas();
        }
        fetchRoles();
    }, [forcedContratistaId, initialSubView]);

    const setupForcedView = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas/${forcedContratistaId}`);
            if (response.ok) {
                const contractor = await response.json();
                setSelectedContratista(contractor);
                setActiveSubView(initialSubView);
                if (initialSubView === 'usuarios') await fetchUsuarios(forcedContratistaId);
                // if (initialSubView === 'personal') // No longer needed here as PersonalManager handles its own fetching
            }
        } catch (err) {
            setError('Error al cargar datos del contratista');
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/roles`);
            if (response.ok) setRoles(await response.json());
        } catch (err) { console.error(err); }
    };

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchContratistas = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas?empresa_id=${usuario.empresa_id}`);
            if (response.ok) {
                const data = await response.json();
                setContratistas(data);
            }
        } catch (err) {
            setError('Error al cargar contratistas');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = editingId
                ? `${API_BASE_URL}/api/contratistas/${editingId}`
                : `${API_BASE_URL}/api/contratistas`;

            const method = editingId ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, empresa_id: usuario.empresa_id })
            });

            if (response.ok) {
                fetchContratistas();
                setShowForm(false);
                setEditingId(null);
                setFormData({ identificador: '', nombre: '', razon_social: '', habilitado: true });
            } else {
                const data = await response.json();
                setError(data.error || 'Error al guardar');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (c) => {
        setEditingId(c.id);
        setFormData({
            identificador: c.identificador,
            nombre: c.nombre,
            razon_social: c.razon_social,
            habilitado: c.habilitado
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este contratista?')) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas/${id}`, { method: 'DELETE' });
            if (response.ok) fetchContratistas();
        } catch (err) {
            setError('Error al eliminar');
        }
    };

    const fetchUsuarios = async (id) => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas/${id}/usuarios`);
            if (response.ok) setUsuarios(await response.json());
        } catch (err) { console.error(err); }
    };

    const fetchExpediente = async (contractor) => {
        setLoadingExpediente(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos/entidad/E/${contractor.id}?empresa_id=${usuario.empresa_id}`);
            if (response.ok) {
                setExpedienteData(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar expediente:', err);
        } finally {
            setLoadingExpediente(false);
        }
    };

    const handleOpenExpediente = (contractor) => {
        setSelectedForExpediente(contractor);
        fetchExpediente(contractor);
    };

    const handleUploadDocument = async (tipoId, file, nombreDoc) => {
        if (!file) return;

        setUploadingDoc(tipoId);
        const data = new FormData();
        data.append('archivo', file);
        data.append('titulo', `${nombreDoc} - ${selectedForExpediente.nombre}`);
        data.append('tipo_documento_id', tipoId);
        data.append('id_contratista', selectedForExpediente.id);
        data.append('entidad_id', selectedForExpediente.id);
        data.append('entidad_tipo', 'E');

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos`, {
                method: 'POST',
                body: data
            });

            if (response.ok) {
                alert('Documento subido y analizado con éxito');
                fetchExpediente(selectedForExpediente);
                fetchContratistas();
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Error al subir');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setUploadingDoc(null);
        }
    };

    const handleUserSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingUser
                ? `${API_BASE_URL}/api/usuarios/${editingUser.id}`
                : `${API_BASE_URL}/api/contratistas/${selectedContratista.id}/usuarios`;

            const method = editingUser ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userFormData)
            });

            if (response.ok) {
                fetchUsuarios(selectedContratista.id);
                setShowUserForm(false);
                setEditingUser(null);
                setUserFormData({ nombre: '', correo: '' });
            } else {
                const data = await response.json();
                setError(data.error || 'Error al procesar usuario');
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este acceso?')) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/usuarios/${id}`, { method: 'DELETE' });
            if (response.ok) fetchUsuarios(selectedContratista.id);
        } catch (err) { setError('Error al eliminar'); }
    };

    // ==================== SUCURSALES ====================
    const fetchSucursales = async (id) => {
        setLoadingSucursales(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas/${id}/sucursales`);
            if (response.ok) {
                setSucursales(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar sucursales:', err);
            setError('Error al cargar sucursales');
        } finally {
            setLoadingSucursales(false);
        }
    };

    const handleSucursalSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingSucursal
                ? `${API_BASE_URL}/api/contratistas/${selectedContratista.id}/sucursales/${editingSucursal.id}`
                : `${API_BASE_URL}/api/contratistas/${selectedContratista.id}/sucursales`;
            const method = editingSucursal ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sucursalFormData)
            });

            if (response.ok) {
                fetchSucursales(selectedContratista.id);
                setShowSucursalForm(false);
                setEditingSucursal(null);
                setSucursalFormData({ sucursal: '', direccion: '', activo: true });
            } else {
                const data = await response.json();
                setError(data.error || 'Error al guardar sucursal');
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const handleEditSucursal = (s) => {
        setEditingSucursal(s);
        setSucursalFormData({
            sucursal: s.sucursal,
            direccion: s.direccion || '',
            activo: s.activo
        });
        setShowSucursalForm(true);
    };

    const handleDeleteSucursal = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta sucursal?')) return;
        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/api/contratistas/${selectedContratista.id}/sucursales/${id}`,
                { method: 'DELETE' }
            );
            if (response.ok) fetchSucursales(selectedContratista.id);
        } catch (err) {
            setError('Error al eliminar sucursal');
        }
    };

    const viewSubView = async (c, view) => {
        setSelectedContratista(c);
        setActiveSubView(view);
        if (view === 'usuarios') await fetchUsuarios(c.id);
        if (view === 'sucursales') await fetchSucursales(c.id);
    };

    if (selectedContratista) {
        return (
            <div className="manager-details" style={{ animation: 'fadeIn 0.3s ease-out' }}>
                {!forcedContratistaId && (
                    <button onClick={() => { setSelectedContratista(null); setActiveSubView('list'); }} className="back-button">
                        <span style={{ fontSize: '1.2rem' }}>←</span> Volver al listado
                    </button>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                    <div>
                        <h2 style={{ fontSize: '2rem', marginBottom: '8px' }}>
                            {activeSubView === 'usuarios' ? '👥 Accesos:' :
                                activeSubView === 'vehiculos' ? '🚗 Vehículos:' :
                                activeSubView === 'sucursales' ? '🏢 Sucursales:' : '👷 Nómina:'} {selectedContratista.nombre}
                        </h2>
                        <span style={{ color: 'var(--text-muted)' }}><strong>RUT:</strong> {selectedContratista.identificador}</span>
                    </div>
                </div>

                {error && <div className="error-message" style={{ marginBottom: '24px' }}>{error}</div>}

                {activeSubView === 'usuarios' && (
                    <section className="dashboard-card" style={{ background: 'var(--bg-card)', padding: '24px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '1.2rem' }}>Usuarios de Acceso</h3>
                            <button onClick={() => { setShowUserForm(true); setEditingUser(null); setUserFormData({ nombre: '', correo: '', rol_id: '' }); }} className="add-button" style={{ padding: '8px 16px' }}>
                                + Nuevo Acceso
                            </button>
                        </div>

                        {showUserForm && (
                            <div className="modal">
                                <form onSubmit={handleUserSubmit} className="standard-form" style={{ width: '100%', maxWidth: '600px' }}>
                                    <h3 style={{ marginBottom: '24px', color: 'var(--sidebar-bg)', fontSize: '1.5rem' }}>
                                        {editingUser ? '📝 Editar' : '👥 Nuevo'} Acceso
                                    </h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div className="form-group">
                                            <label>Nombre Completo</label>
                                            <input
                                                placeholder="Nombre del usuario"
                                                value={userFormData.nombre}
                                                onChange={e => setUserFormData({ ...userFormData, nombre: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Correo Electrónico</label>
                                            <input
                                                placeholder="ejemplo@empresa.com"
                                                type="email"
                                                value={userFormData.correo}
                                                onChange={e => setUserFormData({ ...userFormData, correo: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Rol</label>
                                            <select
                                                value={userFormData.rol_id || ''}
                                                onChange={e => setUserFormData({ ...userFormData, rol_id: e.target.value })}
                                                required
                                            >
                                                <option value="">Seleccionar rol...</option>
                                                {roles.map(r => (
                                                    <option key={r.id} value={r.id}>{r.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-actions" style={{ marginTop: '24px' }}>
                                        <button type="button" onClick={() => { setShowUserForm(false); setEditingUser(null); }} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                            Cancelar
                                        </button>
                                        <button type="submit" className="add-button">
                                            {editingUser ? 'Actualizar Acceso' : 'Crear Acceso'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Correo</th>
                                    <th>Rol</th>
                                    <th>Fecha Creación</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuarios.map(u => (
                                    <tr key={u.id}>
                                        <td data-label="Nombre" style={{ fontWeight: '600' }}>{u.nombre}</td>
                                        <td data-label="Correo">{u.correo}</td>
                                        <td data-label="Rol">{u.rol_nombre || 'Sin rol'}</td>
                                        <td data-label="Fecha Creación" style={{ fontSize: '0.85rem', color: '#64748b' }}>{new Date(u.fecha_creacion).toLocaleDateString()}</td>
                                        <td data-label="Acciones">
                                            <button onClick={() => { setEditingUser(u); setUserFormData({ nombre: u.nombre, correo: u.correo, rol_id: u.rol_id || '' }); setShowUserForm(true); }} className="btn-action btn-edit">📝</button>
                                            <button onClick={() => handleDeleteUser(u.id)} className="btn-action btn-delete">🗑️</button>
                                        </td>
                                    </tr>
                                ))}
                                {usuarios.length === 0 && (
                                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>No hay usuarios de acceso registrados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </section>
                )}

                {activeSubView === 'personal' && (
                    <PersonalManager
                        usuario={usuario}
                        contratistaId={selectedContratista.id}
                        contratistaNombre={selectedContratista.nombre}
                    />
                )}

                {activeSubView === 'vehiculos' && (
                    <VehiculosManager
                        usuario={usuario}
                        contratistaId={selectedContratista.id}
                    />
                )}

                {activeSubView === 'sucursales' && (
                    <section className="dashboard-card" style={{ background: 'var(--bg-card)', padding: '24px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '1.2rem' }}>Sucursales</h3>
                            <button
                                onClick={() => {
                                    setShowSucursalForm(true);
                                    setEditingSucursal(null);
                                    setSucursalFormData({ sucursal: '', direccion: '', activo: true });
                                }}
                                className="add-button"
                                style={{ padding: '8px 16px' }}
                            >
                                + Nueva Sucursal
                            </button>
                        </div>

                        {showSucursalForm && (
                            <div className="modal">
                                <form onSubmit={handleSucursalSubmit} className="standard-form" style={{ width: '100%', maxWidth: '600px' }}>
                                    <h3 style={{ marginBottom: '24px', color: 'var(--sidebar-bg)', fontSize: '1.5rem' }}>
                                        {editingSucursal ? '📝 Editar' : '🏢 Nueva'} Sucursal
                                    </h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div className="form-group">
                                            <label>Nombre de Sucursal</label>
                                            <input
                                                placeholder="Ej: Casa Matriz, Sucursal Norte..."
                                                value={sucursalFormData.sucursal}
                                                onChange={e => setSucursalFormData({ ...sucursalFormData, sucursal: e.target.value })}
                                                maxLength={40}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Dirección</label>
                                            <input
                                                placeholder="Dirección de la sucursal"
                                                value={sucursalFormData.direccion}
                                                onChange={e => setSucursalFormData({ ...sucursalFormData, direccion: e.target.value })}
                                                maxLength={30}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={sucursalFormData.activo}
                                                    onChange={e => setSucursalFormData({ ...sucursalFormData, activo: e.target.checked })}
                                                />
                                                Activa
                                            </label>
                                        </div>
                                    </div>
                                    <div className="form-actions" style={{ marginTop: '24px' }}>
                                        <button
                                            type="button"
                                            onClick={() => { setShowSucursalForm(false); setEditingSucursal(null); }}
                                            className="btn-secondary"
                                            style={{ marginRight: 'auto' }}
                                        >
                                            Cancelar
                                        </button>
                                        <button type="submit" className="add-button">
                                            {editingSucursal ? 'Actualizar Sucursal' : 'Crear Sucursal'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {loadingSucursales ? (
                            <p>Cargando sucursales...</p>
                        ) : (
                            <table className="standard-table">
                                <thead>
                                    <tr>
                                        <th>Sucursal</th>
                                        <th>Dirección</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sucursales.map(s => (
                                        <tr key={s.id}>
                                            <td data-label="Sucursal" style={{ fontWeight: '600' }}>{s.sucursal}</td>
                                            <td data-label="Dirección">{s.direccion || '---'}</td>
                                            <td data-label="Estado">
                                                <span className={`status-badge ${s.activo ? 'valid' : 'invalid'}`} style={{ fontSize: '0.7rem' }}>
                                                    {s.activo ? 'Activa' : 'Inactiva'}
                                                </span>
                                            </td>
                                            <td data-label="Acciones">
                                                <button
                                                    onClick={() => handleEditSucursal(s)}
                                                    className="btn-action btn-edit"
                                                    title="Editar"
                                                >📝</button>
                                                <button
                                                    onClick={() => handleDeleteSucursal(s.id)}
                                                    className="btn-action btn-delete"
                                                    title="Eliminar"
                                                >🗑️</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {sucursales.length === 0 && (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                                                No hay sucursales registradas.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </section>
                )}
            </div>
        );
    }

    return (
        <div className="manager-container">
            <div className="header-actions">
                <h2>Directorio de Contratistas</h2>
                {usuario.tipo !== 'C' && (
                    <button onClick={() => { setShowForm(true); setEditingId(null); }} className="add-button">
                        + Registrar Contratista
                    </button>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}

            {showForm && (
                <div className="modal">
                    <form onSubmit={handleSubmit} className="standard-form">
                        <h3>{editingId ? 'Editar' : 'Nuevo'} Contratista</h3>
                        <div className="form-group">
                            <label>Identificador (RUT/ID):</label>
                            <input
                                value={formData.identificador}
                                onChange={e => setFormData({ ...formData, identificador: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Nombre:</label>
                            <input
                                value={formData.nombre}
                                onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Razón Social:</label>
                            <input
                                value={formData.razon_social}
                                onChange={e => setFormData({ ...formData, razon_social: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                            <div>
                                <button type="submit" disabled={loading} className="add-button" style={{ marginRight: '10px' }}>Guardar</button>
                                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
                            </div>
                            {editingId && (
                                <button type="button" onClick={() => { handleDelete(editingId); setShowForm(false); }} className="btn-action btn-delete" style={{ padding: '8px 16px' }}>
                                    🗑️ Eliminar
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            )}

            {loading ? <p>Cargando...</p> : (
                <table className="standard-table">
                    <thead>
                        <tr>
                            <th>ID/RUT</th>
                            <th>Nombre</th>
                            <th>Razón Social</th>
                            <th>Estado</th>
                            <th>Documentación</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {contratistas.map((c, index) => {
                            const isLastRows = index >= contratistas.length - 2;
                            return (
                            <tr key={c.id}>
                                <td data-label="ID/RUT">{c.identificador}</td>
                                <td data-label="Nombre">{c.nombre}</td>
                                <td data-label="Razón Social">{c.razon_social}</td>
                                <td data-label="Estado">{c.habilitado ? 'Habilitado' : 'Deshabilitado'}</td>
                                <td data-label="Documentación">
                                    {c.documentacion ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                                            <span className={`status-badge ${c.documentacion.estado_general === 'COMPLETO' ? 'valid' :
                                                c.documentacion.estado_general === 'VENCIDO' ? 'vencido' :
                                                    (c.documentacion.estado_general === 'PENDIENTE' ? 'warning' : 'invalid')
                                                }`} style={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                {c.documentacion.estado_general}
                                            </span>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                                                {c.documentacion.aprobados}/{c.documentacion.total} Aprobados
                                                {c.documentacion.vencidos > 0 && <span style={{ color: '#ea580c', marginLeft: '4px' }}>({c.documentacion.vencidos} Venc.)</span>}
                                                {c.documentacion.rechazados > 0 && <span style={{ color: 'var(--error)', marginLeft: '4px' }}>({c.documentacion.rechazados} Rech.)</span>}
                                            </div>
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cargando...</span>
                                    )}
                                </td>
                                <td data-label="Acciones" style={{ whiteSpace: 'nowrap' }}>
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const estimatedHeight = 230;
                                                const openUp = rect.bottom + estimatedHeight > window.innerHeight;
                                                const menuWidth = 196;
                                                setOpenMenu(openMenu?.id === c.id ? null : {
                                                    id: c.id,
                                                    top: openUp ? rect.top - estimatedHeight - 8 : rect.bottom + 8,
                                                    left: Math.min(Math.max(8, rect.left), window.innerWidth - menuWidth - 8)
                                                });
                                            }}
                                            className="btn-action btn-details"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            {(loadingExpediente || uploadingDoc !== null) && selectedForExpediente?.id === c.id ? (
                                                <span className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                                            ) : '⚙️'}
                                            Acciones
                                            <span style={{ fontSize: '0.7rem', marginLeft: '2px' }}>▾</span>
                                        </button>

                                        {openMenu?.id === c.id && (
                                            <div
                                                ref={menuRef}
                                                className="contratista-actions-menu"
                                                style={{
                                                    position: 'fixed',
                                                    top: openMenu.top,
                                                    left: openMenu.left,
                                                    background: 'var(--bg-card)',
                                                    border: '1px solid var(--border-light)',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                                    zIndex: 9999,
                                                    minWidth: '180px',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <button onClick={() => { handleOpenExpediente(c); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    📂 Expediente
                                                </button>
                                                <button onClick={() => { viewSubView(c, 'usuarios'); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    👥 Usuarios
                                                </button>
                                                <button onClick={() => { viewSubView(c, 'personal'); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    👷 Personal
                                                </button>
                                                <button onClick={() => { viewSubView(c, 'vehiculos'); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    🚗 Vehículos
                                                </button>
                                                <button onClick={() => { viewSubView(c, 'sucursales'); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    🏢 Sucursales
                                                </button>
                                                {usuario.tipo !== 'C' && (
                                                    <>
                                                        <div style={{ borderTop: '1px solid var(--border-light)', margin: '4px 0' }}></div>
                                                        <button onClick={() => { handleEdit(c); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                            📝 Editar
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {selectedForExpediente && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '1000px', backgroundColor: '#fdfdfd' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                            <div>
                                <h3 style={{ color: 'var(--sidebar-bg)', fontSize: '1.5rem', marginBottom: '4px' }}>
                                    📂 Expediente: {selectedForExpediente.nombre}
                                </h3>
                                <p style={{ color: 'var(--text-muted)' }}>ID/RUT: {selectedForExpediente.identificador} | Razón Social: {selectedForExpediente.razon_social}</p>
                            </div>
                            <button onClick={() => setSelectedForExpediente(null)} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                        </div>

                        {loadingExpediente ? <p>Cargando documentos requeridos...</p> : (
                            <table className="standard-table">
                                <thead>
                                    <tr>
                                        <th>Documento Requerido</th>
                                        <th>Estado</th>
                                        <th>Última Carga</th>
                                        <th>Vencimiento</th>
                                        <th>Acción / Resultado IA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expedienteData.map(doc => (
                                        <tr key={doc.tipo_id}>
                                            <td data-label="Documento" style={{ fontWeight: '500' }}>
                                                {doc.nombre}
                                                {doc.descripcion && <div style={{ fontSize: '0.75rem', fontWeight: '400', color: 'var(--text-muted)' }}>{doc.descripcion}</div>}
                                            </td>
                                            <td data-label="Estado">
                                                <span className={`status-badge ${doc.estado.toLowerCase()}`} style={{ fontSize: '0.7rem' }}>
                                                    {doc.estado}
                                                </span>
                                            </td>
                                            <td data-label="Última Carga" style={{ fontSize: '0.85rem' }}>
                                                {doc.fecha ? new Date(doc.fecha).toLocaleDateString() : '---'}
                                            </td>
                                            <td data-label="Vencimiento" style={{ fontSize: '0.85rem', color: doc.estado === 'VENCIDO' ? '#ea580c' : 'inherit', fontWeight: doc.estado === 'VENCIDO' ? '600' : '400' }}>
                                                {doc.vencimiento ? new Date(doc.vencimiento).toLocaleDateString() : '---'}
                                            </td>
                                            <td data-label="Acciones">
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                                    {doc.detalles && (
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: '250px', fontStyle: 'italic', textAlign: 'right' }}>
                                                            "{doc.detalles}"
                                                        </div>
                                                    )}

                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.png,.jpg,.jpeg"
                                                            id={`upload-E-${doc.tipo_id}`}
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => handleUploadDocument(doc.tipo_id, e.target.files[0], doc.nombre)}
                                                        />
                                                        <button
                                                            disabled={uploadingDoc !== null}
                                                            onClick={() => document.getElementById(`upload-E-${doc.tipo_id}`).click()}
                                                            className="btn-action btn-edit"
                                                            style={{ fontSize: '0.75rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                        >
                                                            {uploadingDoc === doc.tipo_id ? (
                                                                <>
                                                                    <span className="spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', marginRight: '0' }}></span>
                                                                    Procesando...
                                                                </>
                                                            ) : (doc.documento_id ? '🔄 Reemplazar' : '📤 Subir')}
                                                        </button>

                                                        {doc.nombre_archivo && (
                                                            <a
                                                                href={`${API_BASE_URL}/uploads/${doc.nombre_archivo}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: '600', whiteSpace: 'nowrap' }}
                                                            >
                                                                📂 Ver Documento
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div className="form-actions" style={{ marginTop: '24px' }}>
                            <button type="button" onClick={() => setSelectedForExpediente(null)} className="btn-secondary">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
